import type { FurniturePart } from "../../types";
import { circle, clamp, part, pill, rect, resetPartIds, seam } from "../geometry";
import { str, type BuildContext, type BuildResult, type FurnitureTemplate } from "./types";

export const fridgeTemplate: FurnitureTemplate = {
  id: "fridge",
  label: "Refrigerator",
  frontKind: "door",
  defaultMaterial: "metal",
  params: [
    {
      id: "doors",
      label: "Doors",
      type: "enum",
      default: "auto",
      options: [
        { value: "auto", label: "Auto" },
        { value: "single", label: "Single" },
        { value: "french", label: "French / side-by-side" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const doorsParam = str(params, "doors", "auto");
    const twoDoors = doorsParam === "french" || (doorsParam === "auto" && w >= 33);
    const band = 1.5;
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: 1.25 }));
    const seams = twoDoors ? [seam(0, d / 2 - band, 0, d / 2)] : [];
    parts.push(part("door-face", rect(-w / 2, d / 2 - band, w, band), 0, h + 0.01, { seams }));
    // Handles.
    if (twoDoors) {
      parts.push(part("detail", pill(-2.2, d / 2 - band - 1, 3, 0.7), h, h + 0.5));
      parts.push(part("detail", pill(2.2, d / 2 - band - 1, 3, 0.7), h, h + 0.5));
    } else {
      parts.push(part("detail", pill(-w / 2 + 3.5, d / 2 - band - 1, 4, 0.7), h, h + 0.5));
    }
    return { parts };
  },
};

export const washerTemplate: FurnitureTemplate = {
  id: "washer",
  label: "Washer / dryer",
  frontKind: "door",
  defaultMaterial: "metal",
  params: [
    {
      id: "load",
      label: "Load",
      type: "enum",
      default: "top",
      options: [
        { value: "top", label: "Top load" },
        { value: "front", label: "Front load" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const load = str(params, "load", "top");
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: 1 }));
    // Control panel strip along the back.
    parts.push(part("detail", rect(-w / 2 + 1, -d / 2 + 1, w - 2, 2), h, h + 2));
    if (load === "top") {
      const r = Math.min(w, d) * 0.3;
      parts.push(part("detail", circle(0, 1.5, r), h, h + 0.3, { translucent: true }));
    }
    parts.push(part("door-face", rect(-w / 2, d / 2 - 1.25, w, 1.25), 0, h + 0.01));
    return { parts };
  },
};

export const stoveTemplate: FurnitureTemplate = {
  id: "stove",
  label: "Stove",
  frontKind: "door",
  defaultMaterial: "metal",
  params: [{ id: "burners", label: "Burners", type: "int", min: 2, max: 6, default: 4 }],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const burners = typeof params.burners === "number" ? params.burners : 4;
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: 0.75 }));
    // Back control strip.
    parts.push(part("detail", rect(-w / 2 + 0.75, -d / 2 + 0.75, w - 1.5, 2.25), h, h + 3));
    // Cooktop.
    parts.push(part("top", rect(-w / 2 + 1, -d / 2 + 3.75, w - 2, d - 5.5), h, h + 0.2, { cornerRadius: 0.5 }));
    const r = clamp(Math.min(w, d) * 0.13, 2.5, 4.5);
    const cols = burners <= 3 ? burners : Math.ceil(burners / 2);
    const rows = burners <= 3 ? 1 : 2;
    const cx0 = -w / 2 + 1 + (w - 2) / cols / 2;
    const cy0 = -d / 2 + 3.75 + (d - 5.5) / rows / 2;
    let placed = 0;
    for (let rIdx = 0; rIdx < rows && placed < burners; rIdx++) {
      for (let c = 0; c < cols && placed < burners; c++) {
        const cx = cx0 + ((w - 2) / cols) * c;
        const cy = cy0 + ((d - 5.5) / rows) * rIdx;
        const rr = rIdx === 0 && c % 2 === 0 ? r : r * 0.8;
        parts.push(part("detail", circle(cx, cy, rr), h + 0.2, h + 0.5, { material: { id: "matte", tint: "#2a2a30" } }));
        placed++;
      }
    }
    parts.push(part("door-face", rect(-w / 2, d / 2 - 1.25, w, 1.25), 0, h + 0.01));
    return { parts };
  },
};
